import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Tabs,
  Tab,
  TextField,
  Button,
  IconButton,
  Switch,
  FormControlLabel,
  Tooltip,
  Alert,
  Divider,
  Grid,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Snackbar,
  Menu,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Chip,
  Pagination,
  Card,
  CardContent,
  Stack
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
  DynamicFormOutlined as DynamicFormOutlinedIcon,
  Inbox as InboxIcon,
  MarkEmailRead as MarkReadIcon,
  MarkEmailUnread as MarkUnreadIcon,
  Refresh as RefreshIcon,
  Title as TitleIcon,
  Gavel as GavelIcon,
  ImageOutlined as ImageOutlinedIcon,
  Storefront as StorefrontIcon,
  QrCode2 as QrCode2Icon
} from '@mui/icons-material';
import {
  getCustomForms,
  saveCustomForm,
  deleteCustomForm,
  getCustomFormSubmissions,
  markCustomFormSubmissionRead,
  deleteCustomFormSubmission,
  getLegalDocuments,
  CustomFormData,
  CustomFormField,
  CustomFormFieldType,
  CustomFormSubmission,
  LegalDocumentData
} from '../../../services/ecommerceApi';
import PagePickerDialog from './PagePickerDialog';
import FormDisplayTemplatePicker from './FormDisplayTemplatePicker';
import FormQrDialog from './FormQrDialog';
import {
  DEFAULT_SIDE_TAB_LABEL,
  serializeFormFieldsJson,
  unwrapFormFieldsJson,
  type FormDisplayTemplate,
} from '../utils/formDisplay';

interface FormsSettingsProps {
  companyId: string;
  configId: string;
}

interface FormItem {
  id: string;
  configId: string;
  title: string;
  slug: string;
  description: string;
  submitButtonText: string;
  successMessage: string;
  notifyEmail: string;
  fields: CustomFormField[];
  isActive: boolean;
  displayOrder: number;
  displayTemplate?: FormDisplayTemplate;
  triggerLabel: string;
  showOnHomePage?: boolean;
  tabColor?: string;
  isDirty?: boolean;
  isNew?: boolean;
}

const FIELD_TYPE_OPTIONS: { value: CustomFormFieldType; label: string }[] = [
  { value: 'text', label: 'Tek Satır Metin' },
  { value: 'textarea', label: 'Uzun Metin' },
  { value: 'email', label: 'E-posta' },
  { value: 'phone', label: 'Telefon' },
  { value: 'number', label: 'Sayı' },
  { value: 'city_district', label: 'Şehir + İlçe (Hazır liste)' },
  { value: 'city_branch', label: 'İl + Şube (Kayıtlı şubeler)' },
  { value: 'image', label: 'Resim Yükleme (çoklu)' },
  { value: 'select', label: 'Açılır Liste' },
  { value: 'radio', label: 'Tek Seçim (Radio)' },
  { value: 'checkbox', label: 'Çoklu Seçim (Checkbox)' },
  { value: 'date', label: 'Tarih' },
  { value: 'section', label: 'Bölüm Başlığı' },
  { value: 'consent', label: 'Onay / Beyan (Checkbox)' }
];

const HAS_OPTIONS = (t: CustomFormFieldType) => t === 'select' || t === 'radio' || t === 'checkbox';
const isLayoutField = (t: CustomFormFieldType) => t === 'section';

const CONSENT_PRESETS: Array<Pick<CustomFormField, 'label' | 'linkText' | 'legalTitle'> & { documentType?: string }> = [
  {
    label: 'KVKK Aydınlatma Metnini okudum ve kabul ediyorum.',
    linkText: 'KVKK Aydınlatma Metni',
    legalTitle: 'KVKK Aydınlatma Metni',
    documentType: 'kvkk'
  },
  {
    label: 'Mesafeli Satış Sözleşmesini okudum ve kabul ediyorum.',
    linkText: 'Mesafeli Satış Sözleşmesi',
    legalTitle: 'Mesafeli Satış Sözleşmesi',
    documentType: 'sales-contract'
  },
  {
    label: 'Ön Bilgilendirme Formunu okudum ve kabul ediyorum.',
    linkText: 'Ön Bilgilendirme Formu',
    legalTitle: 'Ön Bilgilendirme Formu'
  },
  {
    label: 'Gizlilik Politikasını okudum ve kabul ediyorum.',
    linkText: 'Gizlilik Politikası',
    legalTitle: 'Gizlilik Politikası',
    documentType: 'privacy'
  },
  {
    label: 'Ticari elektronik ileti gönderilmesini kabul ediyorum.',
    linkText: '',
    legalTitle: ''
  }
];

const LEGAL_DOC_TYPE_LABEL: Record<string, string> = {
  'cookie-policy': 'Çerez Politikası',
  kvkk: 'KVKK Aydınlatma Metni',
  privacy: 'Gizlilik Sözleşmesi',
  'sales-contract': 'Mesafeli Satış Sözleşmesi',
  'refund-policy': 'İptal ve İade Politikası',
  'terms-of-use': 'Kullanım Koşulları'
};

const matchLegalDocument = (
  docs: LegalDocumentData[],
  preset?: { legalTitle?: string; linkText?: string; documentType?: string }
): LegalDocumentData | undefined => {
  if (!docs.length) return undefined;
  const usable = docs.filter((d) => String(d.slug || '').trim());
  if (preset?.documentType) {
    const byTypeActive = usable.find((d) => d.documentType === preset.documentType && d.isActive !== false);
    if (byTypeActive) return byTypeActive;
    const byType = usable.find((d) => d.documentType === preset.documentType);
    if (byType) return byType;
  }
  const needle = String(preset?.legalTitle || preset?.linkText || '')
    .trim()
    .toLocaleLowerCase('tr-TR');
  if (!needle) return undefined;
  return usable.find((d) => {
    const title = String(d.title || '').toLocaleLowerCase('tr-TR');
    const typeLabel = String(LEGAL_DOC_TYPE_LABEL[d.documentType || ''] || '').toLocaleLowerCase('tr-TR');
    return (title && (title.includes(needle) || needle.includes(title)))
      || (typeLabel && (typeLabel.includes(needle) || needle.includes(typeLabel)));
  });
};

const SECTION_PRESETS = [
  'Kişisel Bilgiler',
  'İletişim Bilgileri',
  'Eğitim Bilgileri',
  'Şirket Bilgileri',
  'Onay ve Beyanlar'
];

// =============================================================================
// Helpers
// =============================================================================

const slugify = (input: string): string => {
  if (!input) return '';
  const replacements: Record<string, string> = {
    ı: 'i', İ: 'i', ş: 's', Ş: 's', ç: 'c', Ç: 'c',
    ğ: 'g', Ğ: 'g', ü: 'u', Ü: 'u', ö: 'o', Ö: 'o'
  };
  let s = input.toLowerCase();
  s = s.split('').map((ch) => replacements[ch] ?? ch).join('');
  s = s.replace(/[^a-z0-9]+/g, '-');
  s = s.replace(/^-+|-+$/g, '');
  return s;
};

const newFieldId = (): string => `f_${Math.random().toString(36).slice(2, 8)}_${Date.now().toString(36)}`;

const formatSubmissionDate = (raw: string): string => {
  if (!raw) return '—';

  const value = String(raw).trim();
  if (!value) return '—';

  // Backend bazı kayıtlarda timezone suffix (Z/+03:00) göndermeyebiliyor.
  // Böyle durumlarda değeri UTC kabul edip TR saatine çeviriyoruz.
  const hasTimezone = /([zZ]|[+\-]\d{2}:\d{2})$/.test(value);
  const normalized = hasTimezone ? value : `${value}Z`;
  const date = new Date(normalized);
  if (Number.isNaN(date.getTime())) return value;

  return new Intl.DateTimeFormat('tr-TR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
    timeZone: 'Europe/Istanbul'
  }).format(date);
};

const mapFormFields = (items: unknown[]): CustomFormField[] =>
  items
    .filter((f) => f && typeof f === 'object')
    .map((f: any) => ({
      id: String(f.id ?? newFieldId()),
      type: (FIELD_TYPE_OPTIONS.some((o) => o.value === f.type) ? f.type : 'text') as CustomFormFieldType,
      label: String(f.label ?? ''),
      placeholder: f.placeholder != null ? String(f.placeholder) : '',
      helpText: f.helpText != null ? String(f.helpText) : '',
      required: !!f.required,
      options: Array.isArray(f.options) ? f.options.map((o: any) => String(o)) : undefined,
      linkText: f.linkText != null ? String(f.linkText) : '',
      legalTitle: f.legalTitle != null ? String(f.legalTitle) : '',
      legalText: f.legalText != null ? String(f.legalText) : '',
      legalDocumentSlug: f.legalDocumentSlug != null ? String(f.legalDocumentSlug) : '',
      legalDocumentTitle: f.legalDocumentTitle != null ? String(f.legalDocumentTitle) : '',
      legalPageSlug: f.legalPageSlug != null ? String(f.legalPageSlug) : '',
      legalPageTitle: f.legalPageTitle != null ? String(f.legalPageTitle) : ''
    }));

const parseFieldsJson = (raw: unknown): {
  fields: CustomFormField[];
  displayTemplate?: FormDisplayTemplate;
  triggerLabel: string;
  showOnHomePage?: boolean;
  tabColor?: string;
} => {
  const unwrapped = unwrapFormFieldsJson(raw);
  return {
    fields: mapFormFields(unwrapped.items),
    displayTemplate: unwrapped.display.displayTemplate,
    triggerLabel: unwrapped.display.triggerLabel,
    showOnHomePage: unwrapped.display.showOnHomePage,
    tabColor: unwrapped.display.tabColor,
  };
};

const normalizeFieldKey = (value: string): string =>
  String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '');

const fromApi = (raw: any): FormItem => {
  const parsed = parseFieldsJson(raw?.fieldsJson);
  return {
    id: String(raw?.id ?? ''),
    configId: String(raw?.configId ?? ''),
    title: String(raw?.title ?? ''),
    slug: String(raw?.slug ?? ''),
    description: String(raw?.description ?? ''),
    submitButtonText: String(raw?.submitButtonText ?? 'Gönder'),
    successMessage: String(raw?.successMessage ?? 'Mesajınız iletildi. Teşekkür ederiz.'),
    notifyEmail: String(raw?.notifyEmail ?? ''),
    fields: parsed.fields,
    isActive: raw?.isActive !== false,
    displayOrder: Number(raw?.displayOrder ?? 0),
    displayTemplate: parsed.displayTemplate,
    triggerLabel: parsed.triggerLabel,
    showOnHomePage: parsed.showOnHomePage,
    tabColor: parsed.tabColor,
  };
};

// =============================================================================
// Component
// =============================================================================

const FormsSettings: React.FC<FormsSettingsProps> = ({ companyId, configId }) => {
  const [forms, setForms] = useState<FormItem[]>([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [snack, setSnack] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'info' }>({
    open: false, message: '', severity: 'success'
  });
  const [confirmDelete, setConfirmDelete] = useState<{ open: boolean; id?: string; isNew?: boolean; title?: string }>({ open: false });
  const [activeSubTab, setActiveSubTab] = useState<'edit' | 'submissions'>('edit');
  const [legalDocuments, setLegalDocuments] = useState<LegalDocumentData[]>([]);
  const [qrOpen, setQrOpen] = useState(false);

  const activeForm = forms[activeIndex];

  // ----- Fetch list -----
  const fetchForms = useCallback(async () => {
    if (!configId) return;
    try {
      setLoading(true);
      setError(null);
      const res = await getCustomForms(configId);
      const items = Array.isArray(res?.data) ? res.data.map(fromApi) : [];
      setForms(items);
      setActiveIndex((prev) => (items.length === 0 ? 0 : Math.min(prev, items.length - 1)));
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Formlar yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  }, [configId]);

  useEffect(() => { fetchForms(); }, [fetchForms]);

  useEffect(() => {
    if (!configId) return;
    let cancelled = false;
    getLegalDocuments(configId)
      .then((res) => {
        if (cancelled) return;
        setLegalDocuments(Array.isArray(res?.data) ? res.data : []);
      })
      .catch(() => {
        if (!cancelled) setLegalDocuments([]);
      });
    return () => {
      cancelled = true;
    };
  }, [configId]);

  // ----- Form mutation helpers -----
  const updateActive = (patch: Partial<FormItem>) => {
    setForms((prev) => prev.map((f, idx) => (idx === activeIndex ? { ...f, ...patch, isDirty: true } : f)));
  };

  const handleAddForm = () => {
    const newItem: FormItem = {
      id: `new_${Date.now()}`,
      configId,
      title: 'Yeni Form',
      slug: '',
      description: '',
      submitButtonText: 'Gönder',
      successMessage: 'Mesajınız iletildi. Teşekkür ederiz.',
      notifyEmail: '',
      fields: [
        { id: newFieldId(), type: 'text', label: 'Ad Soyad', placeholder: '', required: true },
        { id: newFieldId(), type: 'email', label: 'E-posta', placeholder: '', required: true },
        { id: newFieldId(), type: 'phone', label: 'Telefon', placeholder: '', required: false },
        { id: newFieldId(), type: 'textarea', label: 'Mesajınız', placeholder: '', required: true }
      ],
      isActive: true,
      displayOrder: forms.length,
      displayTemplate: 'inline',
      triggerLabel: DEFAULT_SIDE_TAB_LABEL,
      showOnHomePage: false,
      tabColor: '',
      isDirty: true,
      isNew: true
    };
    setForms((prev) => [...prev, newItem]);
    setActiveIndex(forms.length);
    setActiveSubTab('edit');
  };

  const handleSave = async () => {
    if (!activeForm) return;
    if (!activeForm.title.trim()) {
      setSnack({ open: true, message: 'Form başlığı zorunludur', severity: 'error' });
      return;
    }
    if (activeForm.fields.filter((f) => !isLayoutField(f.type)).length === 0) {
      setSnack({ open: true, message: 'En az bir form alanı eklemelisiniz', severity: 'error' });
      return;
    }
    // Field doğrulama
    for (const f of activeForm.fields) {
      if (!f.label.trim()) {
        setSnack({
          open: true,
          message: f.type === 'section' ? 'Her bölüm için bir başlık gereklidir' : 'Her alan için bir etiket (label) gereklidir',
          severity: 'error'
        });
        return;
      }
      if (HAS_OPTIONS(f.type) && (!f.options || f.options.length === 0)) {
        setSnack({ open: true, message: `'${f.label}' alanı için en az bir seçenek girilmelidir`, severity: 'error' });
        return;
      }
    }

    const slugCandidate = (activeForm.slug?.trim() || slugify(activeForm.title));
    if (!slugCandidate) {
      setSnack({ open: true, message: 'Geçerli bir kısa ad (slug) üretilemedi', severity: 'error' });
      return;
    }

    try {
      setSaving(true);
      const payload: CustomFormData = {
        id: activeForm.isNew ? undefined : activeForm.id,
        configId,
        title: activeForm.title.trim(),
        slug: slugCandidate,
        description: activeForm.description?.trim() || undefined,
        submitButtonText: activeForm.submitButtonText?.trim() || 'Gönder',
        successMessage: activeForm.successMessage?.trim() || 'Mesajınız iletildi. Teşekkür ederiz.',
        notifyEmail: activeForm.notifyEmail?.trim() || undefined,
        fieldsJson: serializeFormFieldsJson(activeForm.fields, {
          displayTemplate: activeForm.displayTemplate,
          triggerLabel: activeForm.triggerLabel,
          showOnHomePage: activeForm.showOnHomePage,
          tabColor: activeForm.tabColor,
        }),
        isActive: activeForm.isActive,
        displayOrder: activeForm.displayOrder
      };
      const res = await saveCustomForm(payload);
      const savedRaw = res?.data;
      if (savedRaw) {
        const saved = fromApi(savedRaw);
        setForms((prev) => prev.map((f, idx) => (idx === activeIndex ? { ...saved } : f)));
      }
      setSnack({ open: true, message: 'Form kaydedildi', severity: 'success' });
    } catch (e: any) {
      setSnack({
        open: true,
        message: e?.response?.data?.message || e?.message || 'Kayıt sırasında hata oluştu',
        severity: 'error'
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!confirmDelete.id) return;
    try {
      if (!confirmDelete.isNew) {
        await deleteCustomForm(confirmDelete.id);
      }
      setForms((prev) => prev.filter((f) => f.id !== confirmDelete.id));
      setActiveIndex(0);
      setSnack({ open: true, message: 'Form silindi', severity: 'success' });
    } catch (e: any) {
      setSnack({
        open: true,
        message: e?.response?.data?.message || e?.message || 'Silme sırasında hata oluştu',
        severity: 'error'
      });
    } finally {
      setConfirmDelete({ open: false });
    }
  };

  // ----- Field mutation helpers -----
  const addField = () => {
    const f: CustomFormField = { id: newFieldId(), type: 'text', label: 'Yeni Alan', required: false, placeholder: '' };
    updateActive({ fields: [...(activeForm?.fields || []), f] });
  };
  const addSection = (title?: string) => {
    const f: CustomFormField = {
      id: newFieldId(),
      type: 'section',
      label: title?.trim() || 'Yeni Bölüm',
      required: false
    };
    updateActive({ fields: [...(activeForm?.fields || []), f] });
  };
  const addConsent = (preset?: Pick<CustomFormField, 'label' | 'linkText' | 'legalTitle'> & { documentType?: string }) => {
    const matched = matchLegalDocument(legalDocuments, preset);
    const f: CustomFormField = {
      id: newFieldId(),
      type: 'consent',
      label: preset?.label || 'Okudum ve kabul ediyorum.',
      required: true,
      linkText: preset?.linkText || matched?.title || '',
      legalTitle: preset?.legalTitle || matched?.title || '',
      legalText: '',
      legalDocumentSlug: matched?.slug || '',
      legalDocumentTitle: matched?.title || ''
    };
    updateActive({ fields: [...(activeForm?.fields || []), f] });
  };
  const removeField = (id: string) => {
    if (!activeForm) return;
    updateActive({ fields: activeForm.fields.filter((f) => f.id !== id) });
  };
  const moveField = (index: number, dir: -1 | 1) => {
    if (!activeForm) return;
    const next = [...activeForm.fields];
    const newIdx = index + dir;
    if (newIdx < 0 || newIdx >= next.length) return;
    [next[index], next[newIdx]] = [next[newIdx], next[index]];
    updateActive({ fields: next });
  };
  const updateField = (id: string, patch: Partial<CustomFormField>) => {
    if (!activeForm) return;
    updateActive({
      fields: activeForm.fields.map((f) => (f.id === id ? { ...f, ...patch } : f))
    });
  };

  const previewSlug = useMemo(() => {
    if (!activeForm) return '';
    return activeForm.slug?.trim() || slugify(activeForm.title || '');
  }, [activeForm]);

  // -------------------------------------------------------------------------
  // RENDER
  // -------------------------------------------------------------------------
  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <DynamicFormOutlinedIcon color="primary" />
          <Typography variant="h6">Formlar</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Tooltip
            title={
              !activeForm || activeForm.isNew
                ? 'QR için önce formu kaydedin'
                : 'Okutulunca bu form doğrudan açılır'
            }
          >
            <span>
              <Button
                startIcon={<QrCode2Icon />}
                variant="outlined"
                size="small"
                onClick={() => setQrOpen(true)}
                disabled={loading || !activeForm || !!activeForm.isNew || !(activeForm.slug?.trim() || slugify(activeForm.title || ''))}
              >
                QR Üret
              </Button>
            </span>
          </Tooltip>
          <Button startIcon={<AddIcon />} variant="contained" size="small" onClick={handleAddForm} disabled={loading}>
            Yeni Form
          </Button>
        </Box>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Storefront üzerinde göstermek istediğiniz formları burada tanımlayın
        (örn. Franchise Başvurusu, İletişim). Bu formları "Sayfalar" ekranında zengin metin
        editörünün üzerindeki "Form Ekle" butonuyla içeriğe yerleştirebilirsiniz. Gelen yanıtlar
        "Gelen Cevaplar" sekmesinden görüntülenebilir.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={28} />
        </Box>
      )}

      {!loading && forms.length === 0 && (
        <Box sx={{ p: 4, textAlign: 'center', border: '1px dashed #ccc', borderRadius: 2 }}>
          <Typography color="text.secondary" sx={{ mb: 2 }}>
            Henüz form oluşturmadınız.
          </Typography>
          <Button startIcon={<AddIcon />} variant="contained" onClick={handleAddForm}>
            İlk Formu Oluştur
          </Button>
        </Box>
      )}

      {!loading && forms.length > 0 && (
        <>
          <Tabs
            value={activeIndex}
            onChange={(_, v) => { setActiveIndex(v); setActiveSubTab('edit'); }}
            variant="scrollable"
            scrollButtons="auto"
            sx={{ borderBottom: '1px solid #e2e8f0', mb: 2 }}
          >
            {forms.map((f, idx) => (
              <Tab
                key={f.id}
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <span>{f.title || `Form #${idx + 1}`}</span>
                    {f.isDirty && (
                      <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: 'warning.main' }} />
                    )}
                  </Box>
                }
              />
            ))}
          </Tabs>

          {activeForm && (
            <>
              <Tabs
                value={activeSubTab}
                onChange={(_, v) => setActiveSubTab(v as 'edit' | 'submissions')}
                sx={{ mb: 2 }}
              >
                <Tab value="edit" label="Form Tanımı" icon={<DynamicFormOutlinedIcon />} iconPosition="start" />
                <Tab
                  value="submissions"
                  label="Gelen Cevaplar"
                  icon={<InboxIcon />}
                  iconPosition="start"
                  disabled={!!activeForm.isNew}
                />
              </Tabs>

              {activeSubTab === 'edit' ? (
                <FormEditor
                  form={activeForm}
                  configId={configId}
                  previewSlug={previewSlug}
                  legalDocuments={legalDocuments}
                  onChange={updateActive}
                  onAddField={addField}
                  onAddSection={addSection}
                  onAddConsent={addConsent}
                  onRemoveField={removeField}
                  onMoveField={moveField}
                  onUpdateField={updateField}
                  onSave={handleSave}
                  onDelete={() => setConfirmDelete({
                    open: true, id: activeForm.id, isNew: activeForm.isNew, title: activeForm.title
                  })}
                  saving={saving}
                />
              ) : (
                <SubmissionsPanel
                  formId={activeForm.id}
                  formTitle={activeForm.title}
                  formFields={activeForm.fields}
                />
              )}
            </>
          )}
        </>
      )}

      <Dialog open={confirmDelete.open} onClose={() => setConfirmDelete({ open: false })}>
        <DialogTitle>Formu Sil</DialogTitle>
        <DialogContent>
          <DialogContentText>
            "{confirmDelete.title}" formunu kalıcı olarak silmek istediğinize emin misiniz?
            Bu formun gelen yanıtları da etkilenebilir.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete({ open: false })}>Vazgeç</Button>
          <Button color="error" onClick={handleDelete}>Sil</Button>
        </DialogActions>
      </Dialog>

      {activeForm && !activeForm.isNew && (
        <FormQrDialog
          open={qrOpen}
          onClose={() => setQrOpen(false)}
          companyId={companyId}
          title={activeForm.title}
          slug={activeForm.slug?.trim() || slugify(activeForm.title || '')}
          isActive={activeForm.isActive}
          isDirty={activeForm.isDirty}
        />
      )}

      <Snackbar
        open={snack.open}
        autoHideDuration={3000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snack.severity} variant="filled" onClose={() => setSnack((s) => ({ ...s, open: false }))}>
          {snack.message}
        </Alert>
      </Snackbar>

      <Box component="span" sx={{ display: 'none' }}>{companyId}</Box>
    </Paper>
  );
};

// =============================================================================
// Sub-component: FormEditor
// =============================================================================

interface FormEditorProps {
  form: FormItem;
  configId: string;
  previewSlug: string;
  legalDocuments: LegalDocumentData[];
  onChange: (patch: Partial<FormItem>) => void;
  onAddField: () => void;
  onAddSection: (title?: string) => void;
  onAddConsent: (preset?: Pick<CustomFormField, 'label' | 'linkText' | 'legalTitle'> & { documentType?: string }) => void;
  onRemoveField: (id: string) => void;
  onMoveField: (index: number, dir: -1 | 1) => void;
  onUpdateField: (id: string, patch: Partial<CustomFormField>) => void;
  onSave: () => void;
  onDelete: () => void;
  saving: boolean;
}

const FormEditor: React.FC<FormEditorProps> = ({
  form, configId, previewSlug, legalDocuments, onChange,
  onAddField, onAddSection, onAddConsent, onRemoveField, onMoveField, onUpdateField,
  onSave, onDelete, saving
}) => {
  const [sectionMenuEl, setSectionMenuEl] = useState<null | HTMLElement>(null);
  const [consentMenuEl, setConsentMenuEl] = useState<null | HTMLElement>(null);

  return (
    <Box>
      <Grid container spacing={2}>
        <Grid item xs={12} md={8}>
          <TextField
            label="Form Başlığı"
            fullWidth
            size="small"
            value={form.title}
            onChange={(e) => onChange({ title: e.target.value })}
            placeholder="Örn. Franchise Başvuru Formu"
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            label="Kısa Ad (slug)"
            fullWidth
            size="small"
            value={form.slug}
            onChange={(e) => onChange({ slug: e.target.value })}
            placeholder={previewSlug || 'otomatik-uretilir'}
            helperText={`Kullanılacak slug: ${previewSlug || '—'}`}
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            label="Açıklama (formun üstünde görünür)"
            fullWidth
            size="small"
            multiline
            minRows={2}
            value={form.description}
            onChange={(e) => onChange({ description: e.target.value })}
          />
        </Grid>

        <Grid item xs={12}>
          <FormDisplayTemplatePicker
            value={form.displayTemplate || 'inline'}
            triggerLabel={form.triggerLabel || ''}
            showOnHomePage={form.showOnHomePage === true}
            tabColor={form.tabColor || ''}
            onChange={onChange}
          />
        </Grid>

        <Grid item xs={12} md={4}>
          <TextField
            label="Gönder Butonu Yazısı"
            fullWidth
            size="small"
            value={form.submitButtonText}
            onChange={(e) => onChange({ submitButtonText: e.target.value })}
          />
        </Grid>
        <Grid item xs={12} md={5}>
          <TextField
            label="Başarı Mesajı"
            fullWidth
            size="small"
            value={form.successMessage}
            onChange={(e) => onChange({ successMessage: e.target.value })}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <TextField
            label="Sıralama"
            type="number"
            fullWidth
            size="small"
            value={form.displayOrder}
            onChange={(e) => onChange({ displayOrder: Number(e.target.value) || 0 })}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            label="Bildirim E-postası (opsiyonel)"
            fullWidth
            size="small"
            value={form.notifyEmail}
            onChange={(e) => onChange({ notifyEmail: e.target.value })}
            helperText="Form gönderildiğinde içerik bu e-posta adresine de iletilir."
          />
        </Grid>
        <Grid item xs={12} md={6} sx={{ display: 'flex', alignItems: 'center' }}>
          <FormControlLabel
            control={
              <Switch
                checked={form.isActive}
                onChange={(e) => onChange({ isActive: e.target.checked })}
              />
            }
            label="Aktif"
          />
        </Grid>
      </Grid>

      <Divider sx={{ my: 3 }} />

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Form Alanları</Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button size="small" startIcon={<TitleIcon />} variant="outlined" onClick={(e) => setSectionMenuEl(e.currentTarget)}>
            Bölüm Ekle
          </Button>
          <Button size="small" startIcon={<GavelIcon />} variant="outlined" color="secondary" onClick={(e) => setConsentMenuEl(e.currentTarget)}>
            Onay Ekle
          </Button>
          <Button size="small" startIcon={<AddIcon />} variant="outlined" onClick={onAddField}>
            Alan Ekle
          </Button>
        </Box>
      </Box>
      <Menu anchorEl={sectionMenuEl} open={Boolean(sectionMenuEl)} onClose={() => setSectionMenuEl(null)}>
        {SECTION_PRESETS.map((title) => (
          <MenuItem
            key={title}
            onClick={() => {
              onAddSection(title);
              setSectionMenuEl(null);
            }}
          >
            {title}
          </MenuItem>
        ))}
        <MenuItem
          onClick={() => {
            onAddSection('Yeni Bölüm');
            setSectionMenuEl(null);
          }}
        >
          Özel başlık…
        </MenuItem>
      </Menu>
      <Menu anchorEl={consentMenuEl} open={Boolean(consentMenuEl)} onClose={() => setConsentMenuEl(null)}>
        {CONSENT_PRESETS.map((preset) => (
          <MenuItem
            key={preset.label}
            onClick={() => {
              onAddConsent(preset);
              setConsentMenuEl(null);
            }}
          >
            {preset.linkText || preset.label}
          </MenuItem>
        ))}
        <MenuItem
          onClick={() => {
            onAddConsent();
            setConsentMenuEl(null);
          }}
        >
          Özel onay…
        </MenuItem>
      </Menu>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
        Formu başlıklarla bölümlere ayırabilir, KVKK / mesafeli sözleşme gibi onay kutularını ekleyip Yasal Sözleşmeler’deki belgelere bağlayabilirsiniz.
      </Typography>

      {form.fields.length === 0 && (
        <Alert severity="info">Henüz alan yok. "Alan Ekle" ile başlayın.</Alert>
      )}

      <Stack spacing={2}>
        {form.fields.map((field, idx) => (
          <FieldRow
            key={field.id}
            field={field}
            index={idx}
            total={form.fields.length}
            configId={configId}
            legalDocuments={legalDocuments}
            onMove={onMoveField}
            onRemove={onRemoveField}
            onUpdate={onUpdateField}
          />
        ))}
      </Stack>

      <Divider sx={{ my: 3 }} />

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
        <Button color="error" startIcon={<DeleteIcon />} onClick={onDelete} disabled={saving}>
          Sil
        </Button>
        <Button
          variant="contained"
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
          onClick={onSave}
          disabled={saving}
        >
          {saving ? 'Kaydediliyor…' : 'Kaydet'}
        </Button>
      </Box>
    </Box>
  );
};

// =============================================================================
// Sub-component: FieldRow
// =============================================================================

interface FieldRowProps {
  field: CustomFormField;
  index: number;
  total: number;
  configId: string;
  legalDocuments: LegalDocumentData[];
  onMove: (index: number, dir: -1 | 1) => void;
  onRemove: (id: string) => void;
  onUpdate: (id: string, patch: Partial<CustomFormField>) => void;
}

const FieldRow: React.FC<FieldRowProps> = ({ field, index, total, configId, legalDocuments, onMove, onRemove, onUpdate }) => {
  const showOptions = HAS_OPTIONS(field.type);
  const isSection = field.type === 'section';
  const isConsent = field.type === 'consent';
  const optionsString = (field.options || []).join('\n');
  const [optionsDraft, setOptionsDraft] = useState(optionsString);
  const [pagePickerOpen, setPagePickerOpen] = useState(false);

  useEffect(() => {
    setOptionsDraft(optionsString);
  }, [field.id, optionsString]);

  const parseOptionsInput = (raw: string): string[] =>
    raw
      // Enter ile satır satır girişin yanında / , ; ayraçlarını da destekle.
      .split(/\r?\n|\/|,|;/g)
      .map((s) => s.trim())
      .filter(Boolean);
  const handleTypeChange = (nextType: CustomFormFieldType) => {
    const patch: Partial<CustomFormField> = { type: nextType };

    // Hazır şehir/ilçe alanı seçildiğinde varsayılanları daha anlaşılır hale getir.
    if (nextType === 'city_district') {
      const normalizedLabel = (field.label || '').trim().toLowerCase();
      if (!field.label || normalizedLabel === 'yeni alan') {
        patch.label = 'Şehir / İlçe';
      }
      if (!field.placeholder) {
        patch.placeholder = 'Şehir seçiniz';
      }
    }

    if (nextType === 'city_branch') {
      const normalizedLabel = (field.label || '').trim().toLowerCase();
      if (!field.label || normalizedLabel === 'yeni alan') {
        patch.label = 'Şube';
      }
      if (!field.placeholder) {
        patch.placeholder = 'İl seçiniz';
      }
    }

    if (nextType === 'image') {
      const normalizedLabel = (field.label || '').trim().toLowerCase();
      if (!field.label || normalizedLabel === 'yeni alan') {
        patch.label = 'Resim';
      }
      if (!field.placeholder) {
        patch.placeholder = 'JPG, PNG veya WEBP';
      }
    }

    if (nextType === 'section') {
      patch.required = false;
      const normalizedLabel = (field.label || '').trim().toLowerCase();
      if (!field.label || normalizedLabel === 'yeni alan') {
        patch.label = 'Yeni Bölüm';
      }
    }

    if (nextType === 'consent') {
      patch.required = true;
      const normalizedLabel = (field.label || '').trim().toLowerCase();
      if (!field.label || normalizedLabel === 'yeni alan') {
        patch.label = 'Okudum ve kabul ediyorum.';
      }
    }

    onUpdate(field.id, patch);
  };

  return (
    <Card
      variant="outlined"
      sx={
        isSection
          ? { borderColor: '#93c5fd', bgcolor: '#f8fbff' }
          : isConsent
            ? { borderColor: '#c4b5fd', bgcolor: '#faf8ff' }
            : undefined
      }
    >
      <CardContent>
        {(isSection || isConsent) && (
          <Chip
            size="small"
            label={isSection ? 'Bölüm' : 'Onay / Beyan'}
            color={isSection ? 'primary' : 'secondary'}
            variant="outlined"
            sx={{ mb: 1.5 }}
          />
        )}
        <Grid container spacing={2}>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Alan Türü</InputLabel>
              <Select
                label="Alan Türü"
                value={field.type}
                onChange={(e) => handleTypeChange(e.target.value as CustomFormFieldType)}
              >
                {FIELD_TYPE_OPTIONS.map((o) => (
                  <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={isSection || isConsent ? 8 : 5}>
            <TextField
              size="small"
              fullWidth
              label={isSection ? 'Bölüm Başlığı' : 'Etiket'}
              value={field.label}
              onChange={(e) => onUpdate(field.id, { label: e.target.value })}
              placeholder={isSection ? 'Örn. Kişisel Bilgiler' : undefined}
            />
          </Grid>
          {!isSection && !isConsent && (
            <Grid item xs={12} md={3}>
              <TextField
                size="small"
                fullWidth
                label="Yer Tutucu"
                value={field.placeholder || ''}
                onChange={(e) => onUpdate(field.id, { placeholder: e.target.value })}
                disabled={field.type === 'checkbox' || field.type === 'radio' || field.type === 'image'}
              />
            </Grid>
          )}
          {!isSection && (
            <Grid item xs={12} md={1} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Tooltip title="Zorunlu">
                <Switch
                  checked={field.required}
                  onChange={(e) => onUpdate(field.id, { required: e.target.checked })}
                />
              </Tooltip>
            </Grid>
          )}

          {isConsent && (
            <>
              <Grid item xs={12} md={6}>
                <TextField
                  size="small"
                  fullWidth
                  label="Tıklanabilir metin (opsiyonel)"
                  value={field.linkText || ''}
                  onChange={(e) => onUpdate(field.id, { linkText: e.target.value })}
                  helperText="Örn. KVKK Aydınlatma Metni — tıklanınca sözleşme açılır"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  size="small"
                  fullWidth
                  label="Metin başlığı"
                  value={field.legalTitle || ''}
                  onChange={(e) => onUpdate(field.id, { legalTitle: e.target.value })}
                  placeholder="KVKK Aydınlatma Metni"
                />
              </Grid>
              <Grid item xs={12}>
                <Box
                  sx={{
                    p: 1.5,
                    border: '1px solid',
                    borderColor: field.legalDocumentSlug ? '#c4b5fd' : 'divider',
                    borderRadius: 1,
                    bgcolor: '#f8fafc'
                  }}
                >
                  <Typography variant="subtitle2" sx={{ mb: 0.75 }}>
                    Yasal sözleşme
                  </Typography>
                  <FormControl fullWidth size="small" sx={{ mb: 1 }}>
                    <InputLabel>Yasal Sözleşmeler’den bağla</InputLabel>
                    <Select
                      label="Yasal Sözleşmeler’den bağla"
                      value={field.legalDocumentSlug || ''}
                      onChange={(e) => {
                        const slug = String(e.target.value || '');
                        if (!slug) {
                          onUpdate(field.id, { legalDocumentSlug: '', legalDocumentTitle: '' });
                          return;
                        }
                        const doc = legalDocuments.find((d) => d.slug === slug);
                        onUpdate(field.id, {
                          legalDocumentSlug: slug,
                          legalDocumentTitle: doc?.title || '',
                          legalTitle: field.legalTitle || doc?.title || '',
                          linkText: field.linkText || doc?.title || '',
                          legalPageSlug: '',
                          legalPageTitle: ''
                        });
                      }}
                    >
                      <MenuItem value="">
                        <em>Sözleşme seçilmedi</em>
                      </MenuItem>
                      {legalDocuments
                        .filter((d) => String(d.slug || '').trim())
                        .map((doc) => (
                          <MenuItem key={doc.id || doc.slug} value={doc.slug}>
                            {doc.title || LEGAL_DOC_TYPE_LABEL[doc.documentType || ''] || doc.slug}
                            {doc.isActive === false ? ' (Pasif)' : ''}
                          </MenuItem>
                        ))}
                    </Select>
                  </FormControl>
                  {legalDocuments.length === 0 && (
                    <Alert severity="info" sx={{ mb: 1.5 }}>
                      Henüz yasal belge yok. E-Ticaret → Yasal Sözleşmeler sekmesinden KVKK, gizlilik veya satış sözleşmesi oluşturun; ardından buradan bağlayın.
                    </Alert>
                  )}
                  {field.legalDocumentSlug && (() => {
                    const bound = legalDocuments.find((d) => d.slug === field.legalDocumentSlug);
                    return (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1.5 }}>
                        <Chip
                          size="small"
                          color={bound?.isActive === false ? 'warning' : 'secondary'}
                          variant="outlined"
                          label={`Bağlı: ${field.legalDocumentTitle || bound?.title || field.legalDocumentSlug}`}
                        />
                        {bound?.isActive === false && (
                          <Typography variant="caption" color="warning.main">
                            Belge pasif. Mağazada açılması için Yasal Sözleşmeler’den yayına alın.
                          </Typography>
                        )}
                      </Box>
                    );
                  })()}
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                    Kullanıcı onay metnindeki bağlantıya tıklayınca seçilen yasal sözleşmenin içeriği açılır.
                  </Typography>
                  <Divider sx={{ my: 1.5 }} />
                  <Typography variant="subtitle2" sx={{ mb: 0.75 }}>
                    Alternatif kaynak
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => setPagePickerOpen(true)}
                      disabled={!configId}
                    >
                      {field.legalPageSlug
                        ? `Sayfa: ${field.legalPageTitle || field.legalPageSlug}`
                        : 'Özel sayfa seç'}
                    </Button>
                    {field.legalPageSlug && (
                      <Button
                        size="small"
                        color="inherit"
                        onClick={() => onUpdate(field.id, { legalPageSlug: '', legalPageTitle: '' })}
                      >
                        Sayfayı kaldır
                      </Button>
                    )}
                  </Box>
                  <TextField
                    fullWidth
                    multiline
                    minRows={3}
                    label="Elle yazılan metin (opsiyonel)"
                    value={field.legalText || ''}
                    onChange={(e) => onUpdate(field.id, { legalText: e.target.value })}
                    helperText={
                      field.legalDocumentSlug
                        ? 'Yasal sözleşme seçiliyse önce o belge gösterilir. Belge bulunamazsa bu metin kullanılır.'
                        : field.legalPageSlug
                          ? 'Sayfa seçiliyse önce sayfa içeriği gösterilir. Sayfa bulunamazsa bu metin kullanılır.'
                          : 'Sözleşme veya sayfa seçmezseniz kullanıcı bu metni görür.'
                    }
                  />
                </Box>
                <PagePickerDialog
                  open={pagePickerOpen}
                  configId={configId}
                  customPagesOnly
                  onClose={() => setPagePickerOpen(false)}
                  onSelect={({ slug, title }) => {
                    onUpdate(field.id, {
                      legalPageSlug: slug,
                      legalPageTitle: title,
                      legalTitle: field.legalTitle || title,
                      linkText: field.linkText || title,
                      legalDocumentSlug: '',
                      legalDocumentTitle: ''
                    });
                  }}
                />
              </Grid>
            </>
          )}

          {showOptions && (
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Seçenekler (her satıra bir tane)"
                value={optionsDraft}
                onChange={(e) => setOptionsDraft(e.target.value)}
                onBlur={() => {
                  const parsed = parseOptionsInput(optionsDraft);
                  onUpdate(field.id, { options: parsed });
                  setOptionsDraft(parsed.join('\n'));
                }}
                onKeyDown={(e) => {
                  // Enter tuşu parent kısayollarına takılmadan alt satıra geçsin.
                  if (e.key === 'Enter') e.stopPropagation();
                }}
                helperText="Enter ile alt satıra geçebilirsiniz. Ayrıca / , ; ile ayırma da desteklenir."
              />
            </Grid>
          )}

          {field.type === 'city_district' && (
            <Grid item xs={12}>
              <Alert severity="info">
                Bu alan tipi site ön yüzünde otomatik şehir ve ilçe listesi gösterir. Seçenek girmeniz gerekmez.
              </Alert>
            </Grid>
          )}

          {field.type === 'city_branch' && (
            <Grid item xs={12}>
              <Alert severity="info" icon={<StorefrontIcon fontSize="inherit" />}>
                Site ön yüzünde önce il, ardından o ile ait kayıtlı şubeler listelenir.
                Şubeler E-Ticaret → Şubeler ekranından çekilir. Seçenek girmeniz gerekmez.
              </Alert>
            </Grid>
          )}

          {field.type === 'image' && (
            <Grid item xs={12}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  px: 2.5,
                  py: 2,
                  border: '1px dashed',
                  borderColor: 'divider',
                  borderRadius: 2,
                  bgcolor: '#f8fafc'
                }}
              >
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: 2,
                    bgcolor: '#fff',
                    border: '1px solid',
                    borderColor: 'divider',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'text.secondary',
                    flexShrink: 0
                  }}
                >
                  <ImageOutlinedIcon />
                </Box>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    Resim yükleme alanı
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Formda sade bir sürükle-bırak alanı açılır. Kullanıcı birden fazla JPG, PNG veya WEBP yükleyebilir.
                  </Typography>
                </Box>
              </Box>
            </Grid>
          )}

          {!isSection && !isConsent && (
            <Grid item xs={12}>
              <TextField
                size="small"
                fullWidth
                label="Yardım metni (opsiyonel)"
                value={field.helpText || ''}
                onChange={(e) => onUpdate(field.id, { helpText: e.target.value })}
              />
            </Grid>
          )}
        </Grid>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <Tooltip title="Yukarı">
              <span>
                <IconButton size="small" onClick={() => onMove(index, -1)} disabled={index === 0}>
                  <ArrowUpwardIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Aşağı">
              <span>
                <IconButton size="small" onClick={() => onMove(index, 1)} disabled={index === total - 1}>
                  <ArrowDownwardIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
          <Tooltip title="Alanı Sil">
            <IconButton size="small" color="error" onClick={() => onRemove(field.id)}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </CardContent>
    </Card>
  );
};

// =============================================================================
// Sub-component: SubmissionsPanel (server-side paginated)
// =============================================================================

interface SubmissionsPanelProps {
  formId: string;
  formTitle: string;
  formFields: CustomFormField[];
}

const SubmissionsPanel: React.FC<SubmissionsPanelProps> = ({ formId, formTitle, formFields }) => {
  const [page, setPage] = useState(1);
  const pageSize = 10;
  const [items, setItems] = useState<CustomFormSubmission[]>([]);
  const [total, setTotal] = useState(0);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    if (!formId || formId.startsWith('new_')) return;
    try {
      setLoading(true);
      setError(null);
      const res = await getCustomFormSubmissions(formId, page, pageSize);
      setItems(Array.isArray(res?.data) ? res.data : []);
      setTotal(Number(res?.totalCount ?? 0));
      setUnread(Number(res?.unreadCount ?? 0));
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Yanıtlar yüklenemedi');
    } finally {
      setLoading(false);
    }
  }, [formId, page]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const fieldLabelMap = useMemo(() => {
    const map: Record<string, string> = {};
    formFields.forEach((f) => {
      if (f?.id && f.type !== 'section') map[f.id] = f.label || f.id;
    });
    return map;
  }, [formFields]);
  const normalizedFieldLabelMap = useMemo(() => {
    const map: Record<string, string> = {};
    formFields.forEach((f) => {
      if (f.type === 'section') return;
      const normalized = normalizeFieldKey(f?.id || '');
      if (normalized) {
        map[normalized] = f.label || f.id;
      }
    });
    return map;
  }, [formFields]);

  const collectImageItems = (v: unknown): Array<{ url: string; fileName: string }> => {
    const toItem = (raw: unknown): { url: string; fileName: string } | null => {
      if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
      const url = String((raw as Record<string, unknown>).url ?? '').trim();
      if (!url) return null;
      return { url, fileName: String((raw as Record<string, unknown>).fileName ?? '').trim() };
    };
    if (Array.isArray(v)) return v.map(toItem).filter((x): x is { url: string; fileName: string } => Boolean(x));
    const single = toItem(v);
    return single ? [single] : [];
  };

  const formatValue = (v: unknown): string => {
    if (typeof v === 'boolean') return v ? 'Onaylandı' : 'Onaylanmadı';
    if (v === 'true') return 'Onaylandı';
    if (v === 'false') return 'Onaylanmadı';
    if (Array.isArray(v)) {
      const imageNames = v
        .map((x) => {
          if (x && typeof x === 'object' && typeof (x as Record<string, unknown>).url === 'string') {
            const fileName = String((x as Record<string, unknown>).fileName ?? '').trim();
            return fileName || String((x as Record<string, unknown>).url);
          }
          return '';
        })
        .filter(Boolean);
      if (imageNames.length > 0) return imageNames.join(', ');
      return v.map((x) => String(x ?? '')).join(', ');
    }
    if (v && typeof v === 'object') {
      const obj = v as Record<string, unknown>;
      if ('url' in obj && typeof obj.url === 'string' && String(obj.url).trim()) {
        const fileName = String(obj.fileName ?? '').trim();
        return fileName || String(obj.url);
      }
      if ('city' in obj || 'district' in obj || 'branch' in obj || 'branchName' in obj) {
        const city = String(obj.city ?? '').trim();
        const district = String(obj.district ?? '').trim();
        const branch = String(obj.branch ?? obj.branchName ?? '').trim();
        if (city && branch) return `${city} / ${branch}`;
        if (city && district) return `${city} / ${district}`;
        if (city) return city;
        if (branch) return branch;
        if (district) return district;
      }
      return Object.entries(obj)
        .map(([k, val]) => `${k}: ${String(val ?? '')}`)
        .join(', ');
    }
    return String(v ?? '');
  };

  const resolveLabelsForEntries = (entries: Array<[string, unknown]>): Record<string, string> => {
    const usedLabels = new Set<string>();
    const unresolvedKeys: string[] = [];
    const resolvedLabelByKey: Record<string, string> = {};

    for (const [key] of entries) {
      const directLabel = fieldLabelMap[key];
      const normalizedLabel = normalizedFieldLabelMap[normalizeFieldKey(key)];
      const resolved = directLabel || normalizedLabel;
      if (resolved) {
        resolvedLabelByKey[key] = resolved;
        usedLabels.add(resolved);
      } else {
        unresolvedKeys.push(key);
      }
    }

    if (unresolvedKeys.length > 0) {
      const fallbackLabels = formFields
        .filter((f) => f.type !== 'section')
        .map((f) => (f.label || '').trim())
        .filter(Boolean)
        .filter((label) => !usedLabels.has(label));

      unresolvedKeys.forEach((key, index) => {
        resolvedLabelByKey[key] = fallbackLabels[index] || `Alan ${index + 1}`;
      });
    }

    return resolvedLabelByKey;
  };

  const getSubmissionPreview = (raw?: string | null): string => {
    if (!raw) return '';
    try {
      const obj = JSON.parse(raw);
      if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return '';
      const entries = Object.entries(obj as Record<string, unknown>);
      if (entries.length === 0) return '';
      const resolvedLabelByKey = resolveLabelsForEntries(entries);
      const normalizeForMatch = (value: string): string =>
        String(value || '')
          .toLocaleLowerCase('tr-TR')
          .replace(/ı/g, 'i')
          .replace(/ş/g, 's')
          .replace(/ğ/g, 'g')
          .replace(/ü/g, 'u')
          .replace(/ö/g, 'o')
          .replace(/ç/g, 'c')
          .replace(/[^a-z0-9]+/g, ' ')
          .replace(/\s+/g, ' ')
          .trim();

      const isNameLike = (label: string): boolean => {
        const normalized = normalizeForMatch(label);
        return (
          (normalized.includes('ad') && normalized.includes('soyad')) ||
          (normalized.includes('isim') && normalized.includes('soyisim')) ||
          normalized.includes('ad soyad') ||
          normalized.includes('full name') ||
          (normalized.includes('name') && normalized.includes('surname'))
        );
      };

      const toPreview = (label: string, value: unknown): string => {
        const textValue = formatValue(value).trim();
        if (!textValue) return '';
        const shortValue = textValue.length > 40 ? `${textValue.slice(0, 40)}...` : textValue;
        return `${label}: ${shortValue}`;
      };

      // 1) Önce ad-soyad benzeri alanı bul.
      for (const [key, value] of entries) {
        const label = resolvedLabelByKey[key] || fieldLabelMap[key] || key;
        if (!isNameLike(label)) continue;
        const preview = toPreview(label, value);
        if (preview) return preview;
      }

      // 2) Bulunamazsa ilk dolu alanı göster (boş görünmesin).
      for (const [key, value] of entries) {
        const label = resolvedLabelByKey[key] || fieldLabelMap[key] || key;
        const preview = toPreview(label, value);
        if (preview) return preview;
      }

      return '';
    } catch {
      return '';
    }
  };

  const renderData = (raw?: string | null) => {
    if (!raw) return <Typography variant="body2" color="text.secondary">(Boş)</Typography>;
    try {
      const obj = JSON.parse(raw);
      const entries = Object.entries(obj);
      if (entries.length === 0) return <Typography variant="body2" color="text.secondary">(Boş)</Typography>;
      const resolvedLabelByKey = resolveLabelsForEntries(entries);

      return (
        <Stack spacing={0.5}>
          {entries.map(([k, v]) => {
            const imageItems = collectImageItems(v);
            return (
            <Box key={k} sx={{ display: 'flex', gap: 1, alignItems: imageItems.length ? 'flex-start' : 'center' }}>
              <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 120, color: 'text.secondary' }}>
                {resolvedLabelByKey[k] || fieldLabelMap[k] || k}:
              </Typography>
              {imageItems.length > 0 ? (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {imageItems.map((item, index) => (
                    <Box key={`${item.url}-${index}`}>
                      <Box
                        component="a"
                        href={item.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{ display: 'inline-block' }}
                      >
                        <Box
                          component="img"
                          src={item.url}
                          alt={item.fileName || `Resim ${index + 1}`}
                          sx={{
                            width: 96,
                            height: 96,
                            objectFit: 'cover',
                            borderRadius: 1.5,
                            border: '1px solid',
                            borderColor: 'divider',
                            display: 'block'
                          }}
                        />
                      </Box>
                      {item.fileName && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, maxWidth: 96 }}>
                          {item.fileName}
                        </Typography>
                      )}
                    </Box>
                  ))}
                </Box>
              ) : (
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                  {formatValue(v)}
                </Typography>
              )}
            </Box>
            );
          })}
        </Stack>
      );
    } catch {
      return <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{raw}</Typography>;
    }
  };

  const toggleRead = async (s: CustomFormSubmission) => {
    try {
      await markCustomFormSubmissionRead(s.id, !s.isRead);
      setItems((prev) => prev.map((it) => (it.id === s.id ? { ...it, isRead: !s.isRead } : it)));
      setUnread((u) => (s.isRead ? u + 1 : Math.max(0, u - 1)));
    } catch {
      // sessizce yutmuyoruz; hata UI'da göstermek için error state'i set edebiliriz
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Bu yanıtı kalıcı olarak silmek istediğinize emin misiniz?')) return;
    try {
      await deleteCustomFormSubmission(id);
      await fetchData();
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Silme hatası');
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          "{formTitle}" - Gelen Cevaplar
        </Typography>
        <Stack direction="row" spacing={1} alignItems="center">
          <Tooltip title="Yenile">
            <span>
              <IconButton size="small" onClick={fetchData} disabled={loading}>
                <RefreshIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Chip size="small" label={`Toplam: ${total}`} />
          <Chip size="small" color="warning" label={`Okunmamış: ${unread}`} />
        </Stack>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={28} />
        </Box>
      )}

      {!loading && items.length === 0 && (
        <Alert severity="info">Henüz gönderim yok.</Alert>
      )}

      <Stack spacing={1}>
        {items.map((s) => {
          const preview = getSubmissionPreview(s.dataJson);
          return (
            <Card key={s.id} variant="outlined" sx={{ borderLeft: s.isRead ? '3px solid #cbd5e1' : '3px solid #f59e0b' }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="body2" color="text.secondary">
                      {formatSubmissionDate(s.createdAt)}
                    </Typography>
                    {preview && (
                      <Typography
                        variant="body2"
                        sx={{
                          mt: 0.25,
                          color: 'text.secondary',
                          maxWidth: 560,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis'
                        }}
                      >
                        Önizleme: {preview}
                      </Typography>
                    )}
                  </Box>
                <Stack direction="row" spacing={0.5}>
                  <Tooltip title={s.isRead ? 'Okunmadı olarak işaretle' : 'Okundu olarak işaretle'}>
                    <IconButton size="small" onClick={() => toggleRead(s)}>
                      {s.isRead ? <MarkUnreadIcon fontSize="small" /> : <MarkReadIcon fontSize="small" />}
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Sil">
                    <IconButton size="small" color="error" onClick={() => handleDelete(s.id)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Button
                    size="small"
                    variant="text"
                    onClick={() => setExpanded((e) => (e === s.id ? null : s.id))}
                  >
                    {expanded === s.id ? 'Gizle' : 'Detay'}
                  </Button>
                </Stack>
              </Box>
              {expanded === s.id && <Box sx={{ mt: 1 }}>{renderData(s.dataJson)}</Box>}
              </CardContent>
            </Card>
          );
        })}
      </Stack>

      {total > pageSize && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <Pagination
            page={page}
            count={totalPages}
            onChange={(_, p) => setPage(p)}
            color="primary"
            size="small"
          />
        </Box>
      )}
    </Box>
  );
};

export default FormsSettings;
